import React from 'react';
import Immutable, { List, OrderedMap } from 'immutable';
import { format } from 'date-fns';
import { useSelector } from 'react-redux';
import { Helmet } from 'react-helmet';
import {
  Image,
  Container,
  WysiwygField,
} from '@uncinc/uncinc-react-kitchen-sink';
import {
  CoreLink,
  Paragraph,
} from '@uncinc/react-drupal-core';
import { useTranslation } from 'react-i18next';
import RelevantContent from '../../../../components/RelevantContent';
import SocialShare from '../../../../components/SocialShare';
import {
  SCREEN_MOBILE,
  SCREEN_NORMAL,
  SCREEN_TABLET,
  OVERVIEW_REDIRECT,
} from '../../../../config/constants';

import './style.scss';
import PrevNext from '../../../../components/PrevNext';

const NodeArticleDetail = ({ entity }) => {
  const {
    t,
  } = useTranslation();
  const currentLanguage = useSelector((state) => state.getIn(['ui', 'language']));

  const title = entity.getIn(['title', 0, 'value'], '');
  const body = entity.getIn(['body', 0, 'processed']);
  const hero = entity.getIn(['field_image', 0, 'styles'], '');
  const createdDate = entity.getIn(['created', 0, 'value'], '');
  const articleTypeName = entity.getIn(['field_article_type', 0, 'entity', 'field_system_name', 0, 'value'], 'news');
  const formattedDate = format(new Date(createdDate), 'd MMMM y');
  const date = entity.getIn(['created', 0, 'value']);
  const subjects = entity.getIn(['field_subjects'], List());
  const subjectIds = subjects.map((subject) => subject.get('target_id')).toJS();
  const entityOverviewUrl = OVERVIEW_REDIRECT[articleTypeName][currentLanguage];

  // Data to render the Paragraph
  const filters = {
    field_subjects_target_id: subjectIds.length > 0 ? [subjectIds.join('+')] : ['all'],
  };

  // setup schema.org Article
  const pathname = useSelector((state) => state.getIn(['router', 'location', 'pathname']));
  const protocol = window.location.protocol;
  const host = window.location.host;
  const url = `${protocol}//${host}${pathname}`;
  const articleLinkedData = useCreateArticleSchema(entity)
    .set('url', url);
  const schemaString = JSON.stringify(articleLinkedData.toJS(), null, 2);

  return (
    <article className={`NodeArticleDetail ${hero ? '' : 'noHero'}`}>
      <Helmet>
        <script type="application/ld+json">
          {schemaString}
        </script>
      </Helmet>
      {/* Start of Hero */}
      <Container>
        <div className="row justify-content-center">
          {hero && (
            <div className="col-12 NodeArticleDetail__heroimage">
              <Image
                lazyload
                includeWebP
                alt={title}
                src={{
                  [SCREEN_MOBILE]: hero.getIn(['crop_3_1']),
                  [SCREEN_TABLET]: hero.getIn(['crop_3_1']),
                  [SCREEN_NORMAL]: hero.getIn(['crop_3_1']),
                }}
              />
            </div>
          )}
          <div className="col-12 col-md-10 col-lg-8">
            <div className="NodeArticleDetail__titlebox">
              <div>
                <CoreLink className="link" to={entityOverviewUrl}>
                  <i className="icon icon-arrow-left icon-variant-1" /> {t(`${articleTypeName}.back_to_overview`)}
                </CoreLink>
              </div>
              <div>
                <h1 className="asH2">{title}</h1>
              </div>
              <SocialShare />
            </div>
            <div className="NodeArticleDetail__timestamp">
              <p><i className="icon icon-calendar" />{t('posted_on')}{formattedDate}</p>
            </div>
          </div>
        </div>

      </Container>
      {/* End of Hero */}

      {/* Start of Body */}
      <Container>
        <div className="NodeArticleDetail__body">
          <div className="row">
            <div className="col-12 col-md-10 offset-md-1 col-lg-8 offset-lg-2">
              <WysiwygField
                property="schema:text"
                html={body}
              />
            </div>
          </div>
        </div>
      </Container>
      {/* End of Body */}


      {/* Start of paragraphs */}
      {entity.get('field_paragraphs', List()).size > 0 ? (
        <div className="node--page--paragraphs-wrapper">
          {entity.get('field_paragraphs', List()).map((paragraph) => {
            const id = paragraph.get('target_id');
            return (
              <Paragraph
                key={`paragraph:${id}`}
                id={id}
                entity={paragraph.get('entity')}
              />
            );
          })}
        </div>
      ) : null}
      {/* End of paragraphs */}

      <Container>
        <div className="NodeArticleDetail__social-share">
          <div className="row">
            <div className="col-12 col-md-10 offset-md-1 col-lg-8 offset-lg-2">
              <SocialShare />
            </div>
          </div>
        </div>
      </Container>

      {/* Start of Relevant stories section */}
      {/* TODO what if empty */}
      <RelevantContent
        title="Nieuws"
        text="Lees hier wat wij allemaal meemaken en wat ons in beweging brengt."
        linkTitle="Lees meer nieuws"
        linkUrl={entityOverviewUrl}
        viewId="relevant_content_by_subjects"
        viewDisplayBlock="block_2"
        viewFilters={filters}
      />
      {/* End of Relevant stories section */}

      {/* Start of PrevNext section */}
      <div className="NodeProjectDetail__prevnext">
        <PrevNext date={date} type="article" articleTypeName={articleTypeName} />
      </div>
      {/* End of PrevNext section */}

    </article>
  );
};

export default NodeArticleDetail;

function useCreateArticleSchema(entity) {
  const metatag = entity.getIn(['metatag', 'value'], Immutable.Map());
  const image = entity.getIn(['field_image', 0, 'styles', 'thumbnail'], null);
  const pathname = useSelector((state) => state.getIn(['router', 'location', 'pathname']));
  const protocol = window.location.protocol;
  const host = window.location.host;
  const url = `${protocol}//${host}${pathname}`;

  const schema = {
    '@context': 'https://schema.org/',
    '@type': 'Article',
    'headline': metatag.getIn(['schema_article_headline', 'attributes', 'content']),
    'description': metatag.getIn(['schema_article_description', 'attributes', 'content']),
    'articleBody': metatag.getIn(['schema_article_body', 'attributes', 'content']),
    'datePublished': metatag.getIn(['schema_article_date_published', 'attributes', 'content']),
    'dateModified': metatag.getIn(['schema_article_date_modified', 'attributes', 'content']),
    'mainEntityOfPage': {
      '@type': 'WebPage',
      '@id': url,
    },
    'image': image,
    'author': metatag.getIn(['schema_article_author_0', 'attributes', 'content']),
    'publisher': metatag.getIn(['schema_article_author_0', 'attributes', 'content']),
  };

  return OrderedMap(schema);
}
